// nurseryPropagation.ts — the organisation's propagation set-up: methods (with
// their stages) and container types. Plan: docs/nursery-propagation-plan.md.

import type { Numeric } from '@/utils/numeric';
import type { LotStage } from './nursery';

/** The kind of propagation a method is. The family decides which steps it offers. */
export type PropagationFamily =
  | 'seed' | 'cutting' | 'tissue_culture' | 'layer' | 'division' | 'bud' | 'graft' | 'bought_in';

export const PROPAGATION_FAMILIES: PropagationFamily[] = [
  'seed', 'cutting', 'tissue_culture', 'layer', 'division', 'bud', 'graft', 'bought_in',
];

export const PROPAGATION_FAMILY_LABELS: Record<PropagationFamily, string> = {
  seed: 'Seed',
  cutting: 'Cuttings',
  tissue_culture: 'Tissue culture',
  layer: 'Layers (stool bed)',
  division: 'Division',
  bud: 'Budding',
  graft: 'Grafting',
  bought_in: 'Bought in',
};

/**
 * The lot stages each family's steps can end in. Mirrors the server's
 * FAMILY_LOT_STAGES (nurseryPropagationSetupService), which refuses anything else.
 */
export const FAMILY_LOT_STAGES: Record<PropagationFamily, LotStage[]> = {
  seed:           ['sown', 'seedling', 'transplanted', 'potted'],
  cutting:        ['struck', 'rooted', 'transplanted', 'potted'],
  tissue_culture: ['deflasked', 'rooted', 'transplanted', 'potted'],
  layer:          ['rooted', 'transplanted', 'potted'],
  division:       ['rooted', 'potted'],
  bud:            ['budded'],
  graft:          ['grafted'],
  bought_in:      ['seedling', 'rooted', 'transplanted', 'potted', 'growing'],
};

/** One step of a method: what the organisation expects to lose, and how long it takes. */
export interface PropagationMethodStage {
  id?: number;
  sort_order?: number;
  name: string;
  /** The lot stage the step produces; null for a step that is not one (stratifying seed). */
  lot_stage: LotStage | null;
  expected_loss_pct: Numeric;
  typical_weeks: Numeric;
}

export interface PropagationMethod {
  id: number;
  organisation_id: number;
  name: string;
  family: PropagationFamily;
  /** For budwood and graftwood: days cut wood stays usable. */
  wood_window_days: number | null;
  notes: string | null;
  sort_order: number;
  active: boolean;
  stages: PropagationMethodStage[];
  created_at: string;
  updated_at: string;
}

export interface PropagationMethodPayload {
  name?: string;
  family?: PropagationFamily;
  wood_window_days?: number | null;
  notes?: string | null;
  sort_order?: number;
  active?: boolean;
  stages?: {
    name: string;
    lot_stage?: LotStage | null;
    expected_loss_pct?: number | null;
    typical_weeks?: number | null;
  }[];
}

/** A tray, cell flat or flask: how many plants one holds. */
export interface ContainerType {
  id: number;
  organisation_id: number;
  name: string;
  cells_per_container: number;
  /** Bench space one takes, for planning. */
  area_m2: Numeric;
  active: boolean;
  created_at: string;
  updated_at: string;
}

export interface ContainerTypePayload {
  name?: string;
  cells_per_container?: number;
  area_m2?: number | null;
  active?: boolean;
}
